// What a founder can do to a record. Company, person and pipeline pages share these, so a stage change asks the same
// questions everywhere. Every one goes to /api/staff/crm, which refuses tokens and writes the audit row.
import { api, ApiError } from '../../../lib'
import { useCopy, useKit } from '../kit'
import { useForm, type FieldDef } from './parts'
import { BLOCKED_STAGES, REASON_MIN, TRACK_LABEL, nameOf, useMeta, useWrite, type Rel, type Stages, type Track } from './lib'

const CHANNELS = ['linkedin', 'email', 'call', 'meeting', 'chat', 'bookface', 'intro', 'other'].map((v) => ({ value: v, label: v === 'linkedin' ? 'LinkedIn' : nameOf(v) }))
const DIRECTIONS = [{ value: 'out', label: 'We reached out' }, { value: 'in', label: 'They reached out' }, { value: 'none', label: 'Neither' }]
const TRACKS = (Object.keys(TRACK_LABEL) as Track[]).map((t) => ({ value: t, label: TRACK_LABEL[t] }))

const clean = (v: Record<string, string>) => Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x.trim() === '' ? null : x.trim()]))
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA') }

/** Marks a DM sent. A message already on record stops for a typed reason, as Send does. */
export function useMarkMessaged() {
  const { confirm } = useKit()
  const write = useWrite()
  return async (personId: string, founder: string) => {
    const path = `/api/staff/crm/people/${personId}/messaged`
    try {
      await api('POST', path, { founder })
      return write('POST', `/api/staff/crm/people/${personId}/connection`, { founder }, 'Marked messaged')
    } catch (e) {
      if (!(e instanceof ApiError) || e.code !== 'blocked') return write('POST', path, { founder })
      const c = await confirm({ title: 'Mark messaged anyway', body: e.message, confirm: 'Mark messaged', danger: true, reason: { label: 'Why you sent another', required: true } })
      return c.ok ? write('POST', path, { founder, override_reason: c.reason }, 'Marked messaged') : null
    }
  }
}

type Who = { companyId?: string | null; personId?: string | null; stages?: Stages }

export function useRecordActions({ companyId, personId, stages }: Who) {
  const form = useForm()
  const write = useWrite()
  const { toast } = useKit()
  const meta = useMeta()
  const owners = meta.data?.owners ?? ['connor', 'malcolm', 'nikhil']
  const stageMap = stages ?? meta.data?.stages
  const ownerOptions = [{ value: '', label: 'Nobody' }, ...owners.map((o) => ({ value: o, label: nameOf(o) }))]
  const about = { company_id: companyId ?? null, person_id: personId ?? null }

  const relField = (rels: Rel[]): FieldDef[] => (rels.length > 1 ? [{ key: 'rel', label: 'Track', kind: 'select', value: rels[0].id, required: true, options: rels.map((r) => ({ value: r.id, label: TRACK_LABEL[r.track] })) }] : [])
  const pick = (rels: Rel[], id: string | undefined) => rels.find((r) => r.id === id) ?? rels[0]

  const note = async (ids?: Who) => {
    const v = await form.open({ title: 'Add a note', confirm: 'Add note', fields: [{ key: 'summary', label: 'Note', kind: 'long', required: true }] })
    if (v) return write('POST', '/api/staff/crm/touches', { company_id: ids?.companyId ?? about.company_id, person_id: ids?.personId ?? about.person_id, channel: 'note', direction: 'none', summary: v.summary }, 'Note added')
    return null
  }

  const touch = async () => {
    const v = await form.open({
      title: 'Log a touch', confirm: 'Log it', fields: [
        { key: 'channel', label: 'How', kind: 'select', value: 'linkedin', required: true, options: CHANNELS },
        { key: 'direction', label: 'Who started it', kind: 'select', value: 'out', required: true, options: DIRECTIONS },
        { key: 'summary', label: 'What happened', kind: 'long', required: true },
        { key: 'at', label: 'When, if not now', kind: 'datetime' },
      ],
    })
    if (v) return write('POST', '/api/staff/crm/touches', { ...about, channel: v.channel, direction: v.direction, summary: v.summary, at: v.at ? new Date(v.at).toISOString() : null }, 'Logged')
    return null
  }

  const stage = async (rels: Rel[]) => {
    if (!rels.length || !stageMap) return toast('Add a track first', { bad: true })
    const first = rels[0]
    const v = await form.open({
      title: 'Change stage', confirm: 'Change stage', fields: [
        ...relField(rels),
        { key: 'stage', label: 'Stage', kind: 'select', value: first.stage, required: true, options: (x) => stageMap[pick(rels, x.rel).track].map((s) => ({ value: s.key, label: s.label })) },
        { key: 'reason', label: 'Why', kind: 'long', min: REASON_MIN, required: (x) => BLOCKED_STAGES.has(x.stage) || BLOCKED_STAGES.has(pick(rels, x.rel).stage) },
      ],
    })
    if (!v) return null
    const r = pick(rels, v.rel)
    if (v.stage === r.stage) return null
    return write('POST', `/api/staff/crm/relationships/${r.id}/stage`, { stage: v.stage, reason: v.reason.trim() || null }, 'Stage changed')
  }

  const owner = async (rels: Rel[]) => {
    if (!rels.length) return toast('Add a track first', { bad: true })
    const v = await form.open({
      title: 'Set owner', confirm: 'Set owner', fields: [
        ...relField(rels),
        { key: 'owner', label: 'Owner', kind: 'select', value: rels[0].owner ?? '', options: ownerOptions, required: false },
      ],
    })
    if (v) return write('PATCH', `/api/staff/crm/relationships/${pick(rels, v.rel).id}`, { owner: v.owner || null }, 'Owner set')
    return null
  }

  const hold = async (rels: Rel[]) => {
    if (!rels.length) return toast('Add a track first', { bad: true })
    const v = await form.open({
      title: 'Set hold', confirm: 'Set hold',
      body: 'A held company needs a typed reason before anyone marks a send to it.',
      fields: [
        ...relField(rels),
        { key: 'hold_until', label: 'Hold until', kind: 'date', minDate: tomorrow(), required: true },
        { key: 'hold_reason', label: 'Why', kind: 'long', required: true, min: REASON_MIN },
      ],
    })
    if (v) return write('PATCH', `/api/staff/crm/relationships/${pick(rels, v.rel).id}`, { hold_until: v.hold_until, hold_reason: v.hold_reason }, 'Held')
    return null
  }

  const clearHold = (r: Rel) => write('PATCH', `/api/staff/crm/relationships/${r.id}`, { hold_until: null }, 'Hold cleared')

  const task = async () => {
    const v = await form.open({
      title: 'Add a task', confirm: 'Add task', fields: [
        { key: 'title', label: 'What needs doing', required: true },
        { key: 'owner', label: 'Owner', kind: 'select', value: meta.data?.me ?? '', options: ownerOptions },
        { key: 'due_on', label: 'Due', kind: 'date' },
      ],
    })
    if (v) return write('POST', '/api/staff/crm/tasks', { ...about, title: v.title, owner: v.owner || null, due_on: v.due_on || null }, 'Task added')
    return null
  }

  const finishTask = (id: string, done = true) => write('POST', `/api/staff/crm/tasks/${id}/done`, { done }, done ? 'Finished' : 'Reopened')

  const doNotContact = async (on: boolean) => {
    if (!on) {
      const c = await form.open({
        title: 'Clear do not contact', confirm: 'Clear it', danger: true,
        body: 'They go back on the send lists.',
        fields: [{ key: 'clear_reason', label: 'Why', kind: 'long', required: true, min: REASON_MIN }],
      })
      if (c) return write('PATCH', `/api/staff/crm/companies/${companyId}`, { do_not_contact: false, clear_reason: c.clear_reason }, 'Do not contact cleared')
      return null
    }
    const v = await form.open({
      title: 'Do not contact', confirm: 'Mark do not contact', danger: true,
      fields: [{ key: 'do_not_contact_reason', label: 'Why', kind: 'long', required: true, min: REASON_MIN }],
    })
    if (v) return write('PATCH', `/api/staff/crm/companies/${companyId}`, { do_not_contact: true, do_not_contact_reason: v.do_not_contact_reason }, 'Marked do not contact')
    return null
  }

  const addPerson = async () => {
    const v = await form.open({
      title: 'Add a person', confirm: 'Add person', fields: [
        { key: 'name', label: 'Name', required: true },
        { key: 'title', label: 'Title' },
        { key: 'email', label: 'Email' },
        { key: 'linkedin_url', label: 'LinkedIn link', placeholder: 'https://www.linkedin.com/in/...' },
        { key: 'phone', label: 'Phone' },
      ],
    })
    if (v) return write('POST', '/api/staff/crm/people', { company_id: companyId ?? null, ...clean(v) }, 'Person added')
    return null
  }

  const addTrack = async (have: Track[]) => {
    const open = TRACKS.filter((t) => !have.includes(t.value))
    if (!open.length) return toast('Already on every track')
    if (!stageMap) return null
    const v = await form.open({
      title: 'Add a track', confirm: 'Add track', fields: [
        { key: 'track', label: 'Track', kind: 'select', value: open[0].value, required: true, options: open },
        { key: 'stage', label: 'Stage', kind: 'select', value: stageMap[open[0].value][0].key, required: true, options: (x) => stageMap[x.track as Track].map((s) => ({ value: s.key, label: s.label })) },
        { key: 'owner', label: 'Owner', kind: 'select', value: meta.data?.me ?? '', options: ownerOptions },
        { key: 'reason', label: 'Why', kind: 'long', min: REASON_MIN, required: (x) => BLOCKED_STAGES.has(x.stage) },
      ],
    })
    if (!v) return null
    if (!stageMap[v.track as Track].some((s) => s.key === v.stage)) return toast('Pick a stage on that track', { bad: true })
    return write('POST', '/api/staff/crm/relationships', { company_id: companyId, track: v.track, stage: v.stage, owner: v.owner || null, reason: v.reason.trim() || null }, 'Track added')
  }

  const editCompany = async (c: Record<string, unknown>) => {
    const s = (k: string) => (c[k] === null || c[k] === undefined ? '' : String(c[k]))
    const v = await form.open({
      title: 'Edit company', fields: [
        { key: 'name', label: 'Name', value: s('name'), required: true },
        { key: 'domain', label: 'Domain', value: s('domain') },
        { key: 'funding_stage', label: 'Funding stage', value: s('funding_stage') },
        { key: 'last_round', label: 'Last round', value: s('last_round') },
        { key: 'total_raised', label: 'Total raised', value: s('total_raised') },
        { key: 'headcount', label: 'Headcount', kind: 'number', value: s('headcount') },
        { key: 'category', label: 'Category', value: s('category') },
        { key: 'hq', label: 'HQ', value: s('hq') },
        { key: 'yc_batch', label: 'YC batch', value: s('yc_batch') },
        { key: 'us_entity', label: 'US entity', kind: 'select', value: s('us_entity') || 'unknown', required: true, options: [{ value: 'unknown', label: 'Unknown' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] },
        { key: 'ai_spend_note', label: 'AI spend note', value: s('ai_spend_note') },
        { key: 'attio_url', label: 'Attio link', value: s('attio_url') },
      ],
    })
    if (!v) return null
    const all: Record<string, unknown> = clean(v)
    all.headcount = v.headcount.trim() ? Number(v.headcount) : null
    const patch = Object.fromEntries(Object.entries(all).filter(([k, x]) => (x ?? '') !== (c[k] ?? '')))     // only what changed
    if (!Object.keys(patch).length) return toast('Nothing changed')
    return write('PATCH', `/api/staff/crm/companies/${companyId}`, patch, 'Saved')
  }

  const editPerson = async (p: Record<string, unknown>) => {
    const s = (k: string) => (p[k] === null || p[k] === undefined ? '' : String(p[k]))
    const v = await form.open({
      title: 'Edit person', fields: [
        { key: 'name', label: 'Name', value: s('name'), required: true },
        { key: 'title', label: 'Title', value: s('title') },
        { key: 'email', label: 'Email', value: s('email') },
        { key: 'linkedin_url', label: 'LinkedIn link', value: s('linkedin_url') },
        { key: 'phone', label: 'Phone', value: s('phone') },
      ],
    })
    if (!v) return null
    const patch = Object.fromEntries(Object.entries(clean(v)).filter(([k, x]) => (x ?? '') !== (p[k] ?? '')))
    if (!Object.keys(patch).length) return toast('Nothing changed')
    return write('PATCH', `/api/staff/crm/people/${personId}`, patch, 'Saved')
  }

  const copy = useCopy()
  const copyPage = async (url: string | null | undefined) => {
    if (!url) return toast('No page yet', { bad: true })
    await copy(url, 'Link copied')
  }

  return { node: form.node, open: form.open, write, note, touch, stage, owner, hold, clearHold, task, finishTask, doNotContact, addPerson, addTrack, editCompany, editPerson, copyPage, ownerOptions, owners }
}
